import { NgIf } from '@angular/common';
import {
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	inject
} from '@angular/core';
import {
	CdkConnectedOverlay,
	ConnectedPosition,
	Overlay,
	OverlayModule
} from '@angular/cdk/overlay';
import { MatTooltipModule } from '@angular/material/tooltip';
import { IconComponent } from 'components';
import {
	ReportInlineEditService,
	ReportInlineTextField
} from '../../../../services/report-inline-edit.service';
import {
	renderRichText,
	serializeRichText,
	typedLinkHref
} from '../../../../services/report-rich-text';

const IS_MAC = /Mac|iPhone|iPad/.test(
	navigator.platform || navigator.userAgent
);

const NO_MARKS: ReadonlySet<string> = new Set();
const MOD = IS_MAC ? '⌘' : 'Ctrl+';

type FormatCommand =
	| 'bold'
	| 'italic'
	| 'insertUnorderedList'
	| 'insertOrderedList';

/**
 * A text on the report that carries formatting — a section's summary, a
 * paragraph or a callout — drawn and typed into as rich text
 * (`report-rich-text.ts`).
 *
 * The same element draws it on every surface, which is what makes the editor,
 * the PDF and the share page show the same thing: its contents are built from
 * the stored text as DOM nodes, never as markup, by `renderRichText`. Outside
 * the editor that is all it does.
 *
 * In the editor the element is also where the reader types, with the
 * browser's own editing: select words and press Bold, Italic or Link on the
 * toolbar above the field (or ⌘B, ⌘I, ⌘K), or turn lines into a bulleted or a
 * numbered list. Enter keeps what was typed — inside a list it starts the next
 * item, and on an empty one it ends the list — Shift+Enter starts a new line
 * and Esc puts back what was there. Paste brings in the words and none of
 * their formatting. When the field lets go, what it holds is read back into
 * the subset and committed through the page's history, like every typed edit.
 *
 * The toolbar sits in an overlay rather than in the field's own box: a section
 * is drawn scaled down to fit the screen, and a toolbar inside it would be
 * scaled down with it.
 */
@Component({
	standalone: true,
	selector: 'app-report-rich-text',
	templateUrl: './report-rich-text.component.html',
	styleUrls: ['./report-rich-text.component.scss'],
	imports: [NgIf, OverlayModule, MatTooltipModule, IconComponent]
})
export class ReportRichTextComponent implements OnInit, OnDestroy {
	/** The stored text, in the rich-text subset. */
	@Input() set text(value: string | null | undefined) {
		this.source = value ?? '';
		// While the reader types, the field is theirs; what they commit is
		// drawn over it when they let go.
		if (!this.editing) {
			this.draw();
		}
	}
	/** Where typing lands. Without one the text is only text. */
	@Input() field: ReportInlineTextField | null = null;
	@Input() editorMode = false;
	/**
	 * Numbers to mark where they stand: the ones the live data has moved on
	 * from. Only the editor passes any.
	 */
	@Input() set marks(value: ReadonlySet<string> | null | undefined) {
		this.marked = value ?? NO_MARKS;
		if (!this.editing) {
			this.draw();
		}
	}
	/** What an empty field says, in the editor. */
	@Input() placeholder = '';
	/**
	 * "Edited" beside text the reader typed. Off for a text block, which is
	 * the reader's own through and through.
	 */
	@Input() showEdited = true;

	/** What the field shows as the reader types — the slash menu reads it. */
	@Output() typing = new EventEmitter<string>();
	/**
	 * A key, before the field acts on it. A listener that handles it calls
	 * `preventDefault()`, and the field then leaves it alone.
	 */
	@Output() keyPressed = new EventEmitter<KeyboardEvent>();
	/** The field let go — kept what was typed or put back what was there. */
	@Output() finished = new EventEmitter<void>();

	@ViewChild('input', { static: true })
	private inputRef!: ElementRef<HTMLElement>;
	@ViewChild('toolbar') private toolbarRef?: ElementRef<HTMLElement>;
	@ViewChild('linkInput') private linkInputRef?: ElementRef<HTMLInputElement>;
	@ViewChild(CdkConnectedOverlay)
	private toolbarOverlay?: CdkConnectedOverlay;

	editing = false;
	atLimit = false;
	limit = 0;
	/** The link form has taken the toolbar's place. */
	linking = false;
	linkValue = '';
	linkError = '';
	/** What the toolbar shows as on, for the selection as it stands. */
	active = {
		bold: false,
		italic: false,
		link: false,
		bullets: false,
		numbers: false,
		selection: false
	};

	readonly hints = {
		bold: `Bold (${MOD}B)`,
		italic: `Italic (${MOD}I)`,
		link: `Link (${MOD}K)`,
		unlink: 'Remove link',
		linkNeedsText: 'Select words to link them',
		bullets: 'Bulleted list',
		numbers: 'Numbered list'
	};

	readonly toolbarPositions: ConnectedPosition[] = [
		{
			originX: 'start',
			originY: 'top',
			overlayX: 'start',
			overlayY: 'bottom',
			offsetY: -8
		},
		{
			originX: 'start',
			originY: 'bottom',
			overlayX: 'start',
			overlayY: 'top',
			offsetY: 8
		}
	];
	readonly scrollStrategy = inject(Overlay).scrollStrategies.reposition();

	private readonly edits = inject(ReportInlineEditService, {
		optional: true
	});
	private readonly cdr = inject(ChangeDetectorRef);
	private source = '';
	private marked: ReadonlySet<string> = NO_MARKS;
	/** What the field read as when it was opened, written out the same way. */
	private baseline = '';
	private savedRange: Range | null = null;

	get editable(): boolean {
		return this.editorMode && !!this.field && !!this.edits;
	}

	/** The reader typed what the field holds now. Marked in the editor only. */
	get edited(): boolean {
		return this.editable && !!this.edits?.isEdited(this.field!.target);
	}

	ngOnInit(): void {
		this.draw();
	}

	ngOnDestroy(): void {
		this.stopListening();
	}

	/** Open the field for typing, with the caret at the end — a block just
	 *  added, or one the slash menu turned into another kind. */
	focusField(): void {
		const input = this.inputRef.nativeElement;
		input.focus();
		placeCaretAtEnd(input);
	}

	/** Empty the field and keep typing in it: what the slash menu does with
	 *  the "/" once an option is picked. */
	clearTyping(): void {
		const input = this.inputRef.nativeElement;
		while (input.firstChild) {
			input.removeChild(input.firstChild);
		}
		this.focusField();
		this.onInput();
	}

	/** Turn the field's lines into a list — the slash menu's two lists. */
	startList(ordered: boolean): void {
		this.format(ordered ? 'insertOrderedList' : 'insertUnorderedList');
	}

	/** Let go without keeping anything: the field shows what it showed. */
	cancel(): void {
		this.finish(false);
	}

	onFocus(): void {
		if (!this.editable || this.editing) {
			return;
		}
		this.editing = true;
		const input = this.inputRef.nativeElement;
		this.baseline = serializeRichText(input);
		this.limit = Math.max(this.field!.maxLength, this.visibleText().length);
		this.atLimit = this.visibleText().length >= this.limit;
		// Bold is <b>, not a styled span, so the field reads back the way the
		// subset writes it.
		document.execCommand('styleWithCSS', false, 'false');
		document.addEventListener('selectionchange', this.onSelectionChange);
		// The editor scrolls inside its own column, which the overlay's own
		// strategy does not hear: the toolbar is moved with the field here.
		document.addEventListener('scroll', this.onScroll, true);
		this.refreshActive();
	}

	onBlur(event: FocusEvent): void {
		// Focus moving into the toolbar's link form is still this edit.
		const next = event.relatedTarget as Node | null;
		if (next && this.toolbarRef?.nativeElement.contains(next)) {
			return;
		}
		this.finish(true);
	}

	onKeydown(event: KeyboardEvent): void {
		if (!this.editing) {
			return;
		}
		this.keyPressed.emit(event);
		if (event.defaultPrevented) {
			return;
		}
		const mod = (event.metaKey || event.ctrlKey) && !event.altKey;
		const key = event.key.toLowerCase();
		if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			this.finish(false);
			this.inputRef.nativeElement.blur();
			return;
		}
		// The subset has no underline, so a shortcut that makes one would make
		// formatting that disappears on the way out.
		if (mod && key === 'u') {
			event.preventDefault();
			return;
		}
		if (mod && key === 'k') {
			event.preventDefault();
			this.toggleLink();
			return;
		}
		if (event.key !== 'Enter' || event.isComposing || event.shiftKey) {
			return;
		}
		if (this.inListItem()) {
			return;
		}
		event.preventDefault();
		this.inputRef.nativeElement.blur();
	}

	/**
	 * Refuse what would take the text past its bound, before it lands — the
	 * caret stays where it is, which a trim after the fact cannot promise.
	 */
	onBeforeInput(event: Event): void {
		const input = event as InputEvent;
		if (!this.editing) {
			return;
		}
		const type = input.inputType ?? '';
		if (!type.startsWith('insert') || type === 'insertFromPaste') {
			return;
		}
		const breaksLine =
			type === 'insertParagraph' || type === 'insertLineBreak';
		const added = breaksLine ? 1 : (input.data ?? '').length;
		if (
			added &&
			this.visibleText().length - selectedLength(this.inputRef) + added >
				this.limit
		) {
			input.preventDefault();
			this.atLimit = true;
		}
	}

	onInput(): void {
		if (!this.editing) {
			return;
		}
		const input = this.inputRef.nativeElement;
		const visible = this.visibleText();
		// A field typed empty keeps the <br> the browser leaves in it, and
		// would never show its placeholder again. A list is kept: the reader
		// has just asked for one.
		if (!visible && !input.querySelector('li')) {
			while (input.firstChild) {
				input.removeChild(input.firstChild);
			}
		}
		this.atLimit = visible.length >= this.limit;
		this.typing.emit(visible);
	}

	onPaste(event: ClipboardEvent): void {
		if (!this.editing) {
			return;
		}
		event.preventDefault();
		const pasted = (
			event.clipboardData?.getData('text/plain') ?? ''
		).replace(/\r\n?/g, '\n');
		const room =
			this.limit -
			(this.visibleText().length - selectedLength(this.inputRef));
		const fitting = pasted.slice(0, Math.max(room, 0));
		if (!fitting) {
			this.atLimit = pasted.length > 0;
			return;
		}
		document.execCommand('insertText', false, fitting);
		this.onInput();
	}

	/** Dropped content would bring its markup with it. */
	onDrop(event: DragEvent): void {
		event.preventDefault();
	}

	/** The toolbar never takes focus from the field, or its buttons would
	 *  lose the words they are meant to format. */
	keepSelection(event: MouseEvent): void {
		if (event.target !== this.linkInputRef?.nativeElement) {
			event.preventDefault();
		}
	}

	format(command: FormatCommand): void {
		if (!this.editing) {
			this.focusField();
		}
		document.execCommand(command, false);
		this.refreshActive();
		this.onInput();
	}

	/** Link the selected words, or unlink the link the caret is in. */
	toggleLink(): void {
		if (!this.editing) {
			return;
		}
		if (this.active.link) {
			document.execCommand('unlink', false);
			this.refreshActive();
			return;
		}
		const selection = window.getSelection();
		if (
			!selection?.rangeCount ||
			selection.isCollapsed ||
			!this.inputRef.nativeElement.contains(selection.anchorNode)
		) {
			return;
		}
		this.savedRange = selection.getRangeAt(0).cloneRange();
		this.linking = true;
		this.linkValue = '';
		this.linkError = '';
		this.cdr.detectChanges();
		this.linkInputRef?.nativeElement.focus();
	}

	onLinkInput(event: Event): void {
		this.linkValue = (event.target as HTMLInputElement).value;
		this.linkError = '';
	}

	onLinkKeydown(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.applyLink();
		} else if (event.key === 'Escape') {
			event.preventDefault();
			event.stopPropagation();
			this.closeLinkForm();
		}
	}

	/** Clicked away from the link form, and not back into the field: the
	 *  edit ends there, with the link it did not get. */
	onLinkBlur(event: FocusEvent): void {
		if (event.relatedTarget === this.inputRef.nativeElement) {
			return;
		}
		this.linking = false;
		this.finish(true);
	}

	private applyLink(): void {
		const href = typedLinkHref(this.linkValue);
		if (!href) {
			this.linkError = 'Use a web address, like lolly.com, or an email.';
			return;
		}
		const stored = this.field?.storedMaxLength;
		if (
			stored &&
			serializeRichText(this.inputRef.nativeElement).length +
				href.length +
				4 >
				stored
		) {
			this.linkError = 'That link is too long to keep in this text.';
			return;
		}
		this.closeLinkForm();
		document.execCommand('createLink', false, href);
		this.refreshActive();
		this.onInput();
	}

	/**
	 * Back to the field, with the words that were selected selected again.
	 * Focus moves before the form goes: a focused element taken out of the
	 * page reports a blur, and this one's would end the edit.
	 */
	private closeLinkForm(): void {
		const input = this.inputRef.nativeElement;
		input.focus();
		if (this.savedRange) {
			const selection = window.getSelection();
			selection?.removeAllRanges();
			selection?.addRange(this.savedRange);
		}
		this.savedRange = null;
		this.linking = false;
		this.cdr.detectChanges();
	}

	private finish(commit: boolean): void {
		if (!this.editing || !this.field) {
			return;
		}
		this.editing = false;
		this.linking = false;
		this.atLimit = false;
		this.stopListening();

		const input = this.inputRef.nativeElement;
		let typed = commit ? serializeRichText(input) : this.baseline;
		const stored = this.field.storedMaxLength;
		// Formatting past what the text may hold — a field of nothing but
		// links — is let go of rather than the words: they are what the
		// reader typed, and they fit.
		if (stored && typed.length > stored) {
			typed = plainTyping(input);
		}
		if (
			typed !== this.baseline &&
			(typed || this.field.allowEmpty) &&
			this.edits!.commit(this.field, typed)
		) {
			this.source = typed;
		}
		// Drawn again either way: what was committed, in the subset's own
		// form, or — Esc, an emptied field, nothing changed — what the field
		// showed before it was clicked.
		this.draw();
		this.finished.emit();
	}

	private draw(): void {
		if (this.inputRef) {
			renderRichText(
				this.inputRef.nativeElement,
				this.source,
				this.marked
			);
		}
	}

	private visibleText(): string {
		return (this.inputRef.nativeElement.innerText ?? '').replace(/\n$/, '');
	}

	private inListItem(): boolean {
		const selection = window.getSelection();
		const anchor = selection?.anchorNode ?? null;
		const element =
			anchor instanceof Element ? anchor : anchor?.parentElement ?? null;
		const item = element?.closest('li');
		return !!item && this.inputRef.nativeElement.contains(item);
	}

	private readonly onSelectionChange = (): void => {
		this.refreshActive();
	};

	private readonly onScroll = (): void => {
		this.toolbarOverlay?.overlayRef?.updatePosition();
	};

	private stopListening(): void {
		document.removeEventListener('selectionchange', this.onSelectionChange);
		document.removeEventListener('scroll', this.onScroll, true);
	}

	private refreshActive(): void {
		const selection = window.getSelection();
		const input = this.inputRef.nativeElement;
		const inside =
			!!selection?.rangeCount && input.contains(selection.anchorNode);
		const anchor = selection?.anchorNode ?? null;
		const element =
			anchor instanceof Element ? anchor : anchor?.parentElement ?? null;
		const link = inside ? element?.closest('a') : null;
		this.active = {
			bold: inside && document.queryCommandState('bold'),
			italic: inside && document.queryCommandState('italic'),
			link: !!link && input.contains(link),
			bullets:
				inside && document.queryCommandState('insertUnorderedList'),
			numbers: inside && document.queryCommandState('insertOrderedList'),
			selection: inside && !selection!.isCollapsed
		};
	}
}

/** The field's words with none of their formatting, in the subset. */
function plainTyping(input: HTMLElement): string {
	const words = input.ownerDocument.createElement('div');
	words.appendChild(input.ownerDocument.createTextNode(input.innerText));
	return serializeRichText(words);
}

function selectedLength(ref: ElementRef<HTMLElement>): number {
	const selection = window.getSelection();
	if (
		!selection?.rangeCount ||
		!ref.nativeElement.contains(selection.anchorNode)
	) {
		return 0;
	}
	return selection.toString().length;
}

function placeCaretAtEnd(element: HTMLElement): void {
	const range = document.createRange();
	range.selectNodeContents(element);
	range.collapse(false);
	const selection = window.getSelection();
	selection?.removeAllRanges();
	selection?.addRange(range);
}
